import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { crafterUrl } from '../utils/urls';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Plus,
  Clock,
  CheckCircle2,
  MessageSquare,
  ChevronRight,
  Euro,
  Calendar,
  Package,
  Truck,
  Star,
  ShoppingBag,
  Sparkles,
  History,
  AlertCircle,
  MapPin,
  Loader2,
  Store,
  ExternalLink,
  X,
  Paperclip,
  Download,
  Eye,
  Copy,
  XCircle,
  AlertTriangle,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { CrystalTracker, CrystalTrackerCompact } from '../components/CrystalTracker';
import ReviewDialog from '../components/ReviewDialog';
import OrdersSectionTabs from '../components/OrdersSectionTabs';
import ShipmentTimeline from '../components/ShipmentTimeline';
import OrderHelpBlock from '../components/OrderHelpBlock';
import { getFeeBreakdown } from '../lib/feeCalculator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDashboardContent({ 
  autoOpenRequestId,
  viewMode = null, // 'orders' | 'requests' | null (show all with tabs)
  ordersFilter = 'active', // 'active' | 'completed'
  requestsFilter = 'open' // 'open' | 'negotiation' | 'accepted'
}) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  // Main tab state - persisted in sessionStorage to survive tab switching
  // If autoOpenRequestId is provided, default to 'richieste' tab
  // If viewMode is set, use it directly
  const [mainTab, setMainTab] = useState(() => {
    if (viewMode === 'orders') return 'ordini';
    if (viewMode === 'requests') return 'richieste';
    if (autoOpenRequestId) return 'richieste';
    return sessionStorage.getItem('clientDashboardTab') || 'ordini';
  });
  
  // Sync mainTab with viewMode changes
  useEffect(() => {
    if (viewMode === 'orders') setMainTab('ordini');
    else if (viewMode === 'requests') setMainTab('richieste');
  }, [viewMode]);
  
  // Handle main tab change with persistence
  const handleMainTabChange = (value) => {
    if (viewMode) return; // Don't allow tab change in single-view mode
    setMainTab(value);
    sessionStorage.setItem('clientDashboardTab', value);
  };
  
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState(null);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);
  const [reviewedProducts, setReviewedProducts] = useState([]);
  const [confirmingDelivery, setConfirmingDelivery] = useState(null);
  const [ordersView, setOrdersView] = useState(ordersFilter); // Initialize from prop
  const [requestsView, setRequestsView] = useState(requestsFilter === 'negotiation' ? 'inprogress' : requestsFilter); // Map filter to view
  
  // Ref to track if initial data has been loaded (prevents reload on tab switch)
  const dataLoadedRef = useRef(false);
  const userIdRef = useRef(null);
  
  // State for artisan preview panel
  const [previewArtisan, setPreviewArtisan] = useState(null);
  
  // State for attachment lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    // Only load data if user exists AND (data hasn't been loaded yet OR user ID changed)
    if (user && (!dataLoadedRef.current || userIdRef.current !== user.id)) {
      userIdRef.current = user.id;
      dataLoadedRef.current = true;
      
      loadRequests();
      loadOrders();
      loadReviewedProducts();
    }
  }, [user]);

  const loadReviewedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('product_id')
        .eq('client_id', user.id);
      
      if (!error && data) {
        setReviewedProducts(data.map(r => r.product_id).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading reviewed products:', error);
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('direct_requests')
        .select(`*, proposals(count)`)
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
      
      // Auto-open proposals for specific request if requestId is provided
      if (autoOpenRequestId && data) {
        const targetRequest = data.find(r => r.id === autoOpenRequestId);
        if (targetRequest) {
          setSelectedRequest(targetRequest);
          loadProposalsForRequest(targetRequest.id);
        }
      }
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`*, crafter:crafter_id(id, full_name, avatar_url, storefront_title)`)
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadProposalsForRequest = async (requestId) => {
    setLoadingProposals(true);
    try {
      // Use backend API to bypass RLS
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/requests/${requestId}/proposals`);
      
      if (response.ok) {
        const data = await response.json();
        setProposals(data.proposals || []);
      } else {
        console.error('Error loading proposals:', response.status);
        setProposals([]);
      }
    } catch (error) {
      console.error('Error loading proposals:', error);
      setProposals([]);
    } finally {
      setLoadingProposals(false);
    }
  };

  const handleViewProposals = async (request) => {
    setSelectedRequest(request);
    await loadProposalsForRequest(request.id);
  };

  const buildCustomCartItem = (request, proposal) => ({
    productId: `custom-${request.id}`,
    title: request.title,
    image: request.attachments?.[0] || proposal.attachments?.[0] || null,
    basePrice: proposal.proposed_price || 0,
    totalPrice: proposal.proposed_price || 0,
    shippingCost: proposal.shipping_cost || 0,
    quantity: 1,
    selectedVariants: {},
    selectedCustomizations: {},
    customizationValues: {
      description: request.description,
      timeline: getTimelineLabel(proposal.estimated_timeline)
    },
    crafterId: proposal.crafter_id,
    crafterName: proposal.profiles?.full_name || proposal.profiles?.storefront_title || 'Artigiano',
    crafter: {
      id: proposal.crafter_id,
      name: proposal.profiles?.full_name || proposal.profiles?.storefront_title || 'Artigiano'
    },
    isCustomOrder: true,
    requestId: request.id,
    proposalId: proposal.id
  });

  const addCustomItemToCart = (customProduct) => {
    const currentCart = JSON.parse(localStorage.getItem('rarifex_cart') || '[]');
    const existingIndex = currentCart.findIndex(item => item.requestId === customProduct.requestId);
    if (existingIndex >= 0) {
      currentCart[existingIndex] = customProduct;
    } else {
      currentCart.push(customProduct);
    }
    localStorage.setItem('rarifex_cart', JSON.stringify(currentCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  // ===== Annullamento ordine (cliente) — processo soft =====
  const [cancelOrderTarget, setCancelOrderTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingOrder, setCancellingOrder] = useState(false);

  const handleCancelOrderClient = async () => {
    if (!cancelOrderTarget) return;
    if (!cancelReason.trim()) {
      toast.error('Indica il motivo dell\'annullamento');
      return;
    }
    setCancellingOrder(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/orders/${cancelOrderTarget.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, role: 'client', reason: cancelReason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Errore durante l\'annullamento');
      toast.success('Ordine annullato. Il rimborso totale è stato avviato.');
      setCancelOrderTarget(null);
      if (typeof loadOrders === 'function') loadOrders();
    } catch (e) {
      toast.error(e.message || 'Impossibile annullare l\'ordine');
    } finally {
      setCancellingOrder(false);
    }
  };

  const handleResumePayment = async (request) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/requests/${request.id}/proposals`);
      const data = response.ok ? await response.json() : { proposals: [] };
      const list = data.proposals || [];
      const accepted = list.find(p => p.id === request.selected_proposal_id)
        || list.find(p => p.status === 'accepted');
      if (!accepted) {
        toast.error('Nessuna proposta accettata trovata per questa richiesta.');
        return;
      }
      addCustomItemToCart(buildCustomCartItem(request, accepted));
      toast.success('Riprendiamo il pagamento: completa l\'ordine dal carrello.');
      navigate('/cart');
    } catch (e) {
      console.error('Error resuming payment:', e);
      toast.error('Impossibile riprendere il pagamento. Riprova.');
    }
  };

  const handleAcceptProposal = async (proposal) => {
    try {
      // Accept via backend (service key) so proposal statuses update despite RLS
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/requests/${selectedRequest.id}/accept-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal_id: proposal.id, user_id: user.id })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error('Error accepting proposal:', err);
        throw new Error(err.detail || 'Errore durante l\'accettazione');
      }

      // Add custom product to cart for payment
      addCustomItemToCart(buildCustomCartItem(selectedRequest, proposal));

      setSelectedRequest(null);
      loadRequests();

      // Show success message and redirect to checkout
      toast.success('Proposta accettata! Vai al checkout per completare l\'ordine.');
      
      // Redirect to cart/checkout page after a brief delay
      setTimeout(() => {
        navigate('/cart');
      }, 500);

    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast.error('Errore nell\'accettazione della proposta');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Aperta';
      case 'in_progress': return 'In Lavorazione';
      case 'completed': return 'Completata';
      case 'cancelled': return 'Annullata';
      default: return status;
    }
  };

  const getPriceRangeLabel = (range) => {
    switch (range) {
      case '100-500': return '€100 - €500';
      case '500-2000': return '€500 - €2.000';
      case 'custom': return 'Budget personalizzato';
      default: return range;
    }
  };

  const getTimelineLabel = (timeline) => {
    if (!timeline) return 'N/D';
    // Handle custom date format
    if (timeline.startsWith('custom:')) {
      return `Data: ${timeline.split(':')[1]}`;
    }
    // Map technical values to human-readable labels
    const timelineMap = {
      '1week': '1 Settimana',
      '2weeks': '2 Settimane',
      '3weeks': '3 Settimane',
      '4weeks': '4 Settimane',
      '1month': '1 Mese',
      '2months': '2 Mesi',
      '3months': '3 Mesi',
      '6months': '6 Mesi',
      'flexible': 'Flessibile',
      '1-2weeks': '1-2 Settimane',
      '1-2days': '1-2 Giorni',
      '3-5days': '3-5 Giorni',
      '3-4weeks': '3-4 Settimane',
      'over-month': 'Oltre 1 mese',
      'urgent': 'Urgente',
      'custom': 'Personalizzato'
    };
    return timelineMap[timeline] || timeline;
  };

  // Stima dei giorni di consegna da un valore timeline (per ordinare "più rapida").
  const timelineToDays = (t) => {
    if (!t) return 999;
    const s = String(t).toLowerCase();
    if (s.startsWith('custom')) return 900;
    if (s.includes('urgent')) return 1;
    if (s.includes('flexible') || s.includes('flessibile')) return 999;
    const m = s.match(/(\d+)(?:\s*-\s*\d+)?\s*(giorn|day|settiman|week|mes|month)/);
    if (m) {
      const n = parseInt(m[1], 10);
      const unit = m[2];
      if (unit.startsWith('giorn') || unit.startsWith('day')) return n;
      if (unit.startsWith('settiman') || unit.startsWith('week')) return n * 7;
      if (unit.startsWith('mes') || unit.startsWith('month')) return n * 30;
    }
    if (s.includes('over-month') || s.includes('oltre')) return 45;
    return 500;
  };

  const handleOpenReviewDialog = (order, product = null) => {
    setSelectedOrderForReview(order);
    setSelectedProductForReview(product);
    setReviewDialogOpen(true);
  };

  const handleReviewSubmitted = () => {
    loadOrders();
    loadReviewedProducts();
  };

  const openRequests = requests.filter(r => r.status === 'open');
  const activeOrders = orders.filter(o => ['confirmed', 'in_progress', 'ready', 'shipped'].includes(o.status));
  const completedOrders = orders.filter(o => ['delivered', 'completed', 'cancelled'].includes(o.status));
  const completedRequests = requests.filter(r => r.status === 'completed' || r.status === 'cancelled');

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confermato';
      case 'in_progress': return 'In Lavorazione';
      case 'ready': return 'Pronto';
      case 'shipped': return 'Spedito';
      case 'delivered': return 'Consegnato';
      case 'completed': return 'Completato';
      case 'cancelled': return 'Annullato';
      default: return status;
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'ready': return 'bg-purple-100 text-purple-700';
      case 'shipped': return 'bg-indigo-100 text-indigo-700';
      case 'delivered': return 'bg-teal-100 text-teal-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Render Cart Order Card
  const renderCartOrderCard = (order, index) => {
    const items = order.items || [];
    const shipping = order.shipping_address || {};
    
    return (
      <motion.div
        key={order.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
      >
        <Card className="p-6 hover:shadow-lg transition-all" data-testid={`cart-order-${order.id}`}>
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ShoppingBag className="w-5 h-5 text-[#008080]" />
                  <span className="text-sm font-mono text-slate-500">
                    {order.order_number || `RFX-${order.id?.slice(0, 8).toUpperCase()}`}
                  </span>
                </div>
                <h3 className="text-lg font-bold">{order.title}</h3>
              </div>
              <Badge className={getOrderStatusColor(order.status)}>
                {getOrderStatusLabel(order.status)}
              </Badge>
            </div>

            {/* Items Preview */}
            <div className="flex gap-3 overflow-x-auto pb-2">
              {items.slice(0, 4).map((item, idx) => (
                <div key={idx} className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <Package className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-1 truncate max-w-[64px]">{item.title}</p>
                  <p className="text-xs font-medium">x{item.quantity}</p>
                </div>
              ))}
              {items.length > 4 && (
                <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                  <span className="text-sm text-slate-500">+{items.length - 4}</span>
                </div>
              )}
            </div>

            {/* Order Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Totale</p>
                <p className="font-bold text-lg">€{(order.total || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-slate-500">Spedizione</p>
                <p className="font-medium capitalize">
                  {order.shipping_method === 'express' ? 'Express' : 'Standard'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Pagamento</p>
                <p className="font-medium capitalize flex items-center gap-1">
                  {order.payment_status === 'paid' ? (
                    <><CheckCircle2 className="w-3 h-3 text-green-600" /> Pagato</>
                  ) : (
                    order.payment_status
                  )}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Data ordine</p>
                <p className="font-medium">
                  {new Date(order.created_at).toLocaleDateString('it-IT')}
                </p>
              </div>
            </div>

            {/* Shipping Address */}
            {shipping.fullName && (
              <div className="text-sm bg-slate-50 rounded-lg p-3">
                <p className="text-slate-500 flex items-center gap-1 mb-1">
                  <MapPin className="w-3 h-3" /> Indirizzo di spedizione
                </p>
                <p className="font-medium">{shipping.fullName}</p>
                <p className="text-slate-600">{shipping.address}, {shipping.postalCode} {shipping.city} ({shipping.province})</p>
              </div>
            )}

            {/* Artigiano + contatto (chat esistente Rarifex) */}
            <div className="text-sm bg-slate-50 rounded-lg p-3 flex items-center justify-between gap-2" data-testid="order-crafter-block">
              <div className="min-w-0">
                <p className="text-slate-500 flex items-center gap-1 mb-0.5">
                  <Store className="w-3 h-3" /> Venduto da
                </p>
                <p className="font-medium truncate" data-testid="order-crafter-name">
                  {order.crafter?.storefront_title || order.crafter?.full_name || 'Artigiano'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleContactCrafter(order)}
                className="shrink-0"
                data-testid="order-contact-crafter-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Contatta l'artigiano
              </Button>
            </div>

            {/* Progress Timeline / Annullamento */}
            {order.status === 'cancelled' ? (
              <div className="pt-4 border-t">
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="client-order-cancelled-note">
                  <p className="font-semibold flex items-center gap-1 mb-0.5">
                    <XCircle className="w-4 h-4" /> Ordine annullato
                  </p>
                  {order.cancellation_reason && (
                    <p className="text-red-700" data-testid="client-order-cancellation-reason">
                      Motivo: {order.cancellation_reason}
                    </p>
                  )}
                  {order.cancelled_by && (
                    <p className="text-red-600 text-xs mt-0.5">
                      Annullato {order.cancelled_by === 'client' ? 'da te' : "dall'artigiano"} · rimborso totale emesso
                    </p>
                  )}
                </div>
              </div>
            ) : (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-xs">
                {['confirmed', 'in_progress', 'shipped', 'delivered'].map((step, idx) => {
                  const steps = ['confirmed', 'in_progress', 'shipped', 'delivered'];
                  const currentIdx = steps.indexOf(order.status);
                  const isCompleted = idx <= currentIdx;
                  const isCurrent = idx === currentIdx;
                  
                  return (
                    <div key={step} className="flex-1 flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full mb-1 ${
                        isCompleted ? 'bg-[#008080]' : 'bg-slate-200'
                      } ${isCurrent ? 'ring-2 ring-[#008080]/30' : ''}`} />
                      <span className={`text-center ${isCompleted ? 'text-[#008080] font-medium' : 'text-slate-400'}`}>
                        {getOrderStatusLabel(step)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {order.status === 'shipped' && (
                <Button 
                  size="sm" 
                  onClick={() => handleMarkDelivered(order)}
                  disabled={confirmingDelivery === order.id}
                  className="bg-teal-600 text-white"
                >
                  {confirmingDelivery === order.id ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Package className="w-4 h-4 mr-1" />
                  )}
                  Ho ricevuto l'ordine
                </Button>
              )}
              
              {order.status === 'delivered' && (
                <Button 
                  size="sm" 
                  onClick={() => handleCompleteOrder(order.id)}
                  className="bg-green-600 text-white"
                >
                  <Star className="w-4 h-4 mr-1" />
                  Completa
                </Button>
              )}

              {!['shipped','delivered','completed','cancelled'].includes(order.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setCancelOrderTarget(order); setCancelReason(''); }}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid={`client-cancel-order-${order.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Annulla ordine
                </Button>
              )}

              {order.tracking_number && (
                <>
                  <Button variant="outline" size="sm">
                    <Truck className="w-4 h-4 mr-1" />
                    Traccia spedizione
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(order.tracking_number);
                        toast.success('Codice tracking copiato negli appunti');
                      } catch {
                        toast.error('Copia non riuscita, copia manualmente il codice');
                      }
                    }}
                    data-testid="order-copy-tracking-btn"
                    title={`Copia tracking: ${order.tracking_number}`}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copia tracking
                  </Button>
                </>
              )}
            </div>

            {/* Timeline Shipment Engine — mostra tracking dettagliato per gli ordini spediti */}
            {['shipped','delivered','completed'].includes(order.status) && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <ShipmentTimeline orderId={order.id} compact />
              </div>
            )}

            {/* Assistenza ordine — azioni contestuali */}
            <OrderHelpBlock order={order} />
          </div>
        </Card>
      </motion.div>
    );
  };

  const handleContactCrafter = (order) => {
    // Navigate to messages with this crafter
    navigate(`/messages?crafter=${order.crafter_id}`);
  };

  const handleMarkDelivered = async (order) => {
    setConfirmingDelivery(order.id);
    try {
      // Get crafter's Stripe Connect account
      const crafterAccountResponse = await fetch(`${API_URL}/api/connect/account-status/${order.crafter_id}`);
      const crafterAccount = await crafterAccountResponse.json();
      
      // Calculate the amount to transfer (order total minus platform fee)
      const orderTotal = order.total || order.agreed_price || 0;
      const feeBreakdown = getFeeBreakdown(orderTotal);
      const platformFee = feeBreakdown.platformFee + feeBreakdown.stripeFee;
      
      // If crafter has Stripe Connect, transfer funds
      if (crafterAccount.has_account && crafterAccount.charges_enabled) {
        try {
          const transferResponse = await fetch(`${API_URL}/api/connect/transfer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_id: order.id,
              crafter_stripe_account_id: crafterAccount.account_id,
              amount: orderTotal,
              platform_fee: platformFee
            })
          });
          
          if (!transferResponse.ok) {
            const error = await transferResponse.json();
            console.warn('Transfer warning:', error.detail);
            // Continue anyway - funds may need manual transfer
          } else {
            console.log('Transfer successful');
          }
        } catch (transferError) {
          console.warn('Transfer error (will require manual processing):', transferError);
        }
      } else {
        console.log('Crafter does not have Stripe Connect - manual transfer required');
      }
      
      // Update order status regardless of transfer success
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: 'delivered', 
          delivered_at: new Date().toISOString(),
          funds_released: crafterAccount.has_account && crafterAccount.charges_enabled
        })
        .eq('id', order.id);

      if (error) throw error;
      
      // Send delivery confirmation email to client
      // Get crafter name for the email
      const { data: crafterProfile } = await supabase
        .from('profiles')
        .select('full_name, business_name')
        .eq('id', order.crafter_id)
        .single();
      
      if (user?.email && profile?.full_name) {
        fetch(`${API_URL}/api/email/order-delivered`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_email: user.email,
            client_name: profile.full_name || 'Cliente',
            order_number: order.order_number,
            crafter_name: crafterProfile?.business_name || crafterProfile?.full_name || 'Artigiano'
          })
        }).catch(err => console.error('Error sending delivered email:', err));
      }
      
      // Send message notification to crafter
      const orderTitle = order.items?.[0]?.title || order.title || 'Ordine';
      fetch(`${API_URL}/api/notifications/order-received`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.id,
          crafter_id: order.crafter_id,
          client_id: user?.id,
          client_name: profile?.full_name || 'Cliente',
          order_number: order.order_number || `ORD-${order.id?.slice(0, 8).toUpperCase()}`,
          order_title: orderTitle,
          frontend_url: window.location.origin
        })
      }).catch(err => console.error('Error sending received notification:', err));
      
      toast.success('Ordine segnato come consegnato! Il pagamento è stato rilasciato all\'artigiano.');
      loadOrders();
    } catch (error) {
      console.error('Error marking delivered:', error);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setConfirmingDelivery(null);
    }
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: 'completed', 
          completed_at: new Date().toISOString() 
        })
        .eq('id', orderId);

      if (error) throw error;

      // Also update the original request
      const order = orders.find(o => o.id === orderId);
      if (order?.request_id) {
        await supabase
          .from('direct_requests')
          .update({ status: 'completed' })
          .eq('id', order.request_id);
      }

      toast.success('Ordine completato! Grazie per aver scelto Rarifex.');
      loadOrders();
      loadRequests();
    } catch (error) {
      toast.error('Errore nel completamento');
    }
  };

  // In-progress requests (requests with proposals but no order yet)
  const inProgressRequests = requests.filter(r => r.status === 'in_progress' && !orders.some(o => o.request_id === r.id && ['confirmed', 'in_progress', 'ready', 'shipped', 'delivered', 'completed'].includes(o.status)));

  return (
    <div data-testid="client-dashboard-content">
      {/* Header - hide in viewMode */}
      {!viewMode && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-800">Area Cliente</h2>
          <Link to="/requests">
            <Button className="bg-[#00CED1] text-white hover:bg-[#00CED1]/90" data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Richiesta
            </Button>
          </Link>
        </div>
      )}

      {/* Stats Cards - hide in viewMode */}
      {!viewMode && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-[#DAA520]/10 to-transparent border-[#DAA520]/20">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-8 h-8 text-[#DAA520]" />
              <div>
                <p className="text-2xl font-bold text-[#DAA520]">{activeOrders.length}</p>
                <p className="text-xs text-slate-600">Ordini Attivi</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-[#10B981]/10 to-transparent border-[#10B981]/20">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              <div>
                <p className="text-2xl font-bold text-[#10B981]">{completedOrders.length}</p>
                <p className="text-xs text-slate-600">Completati</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-[#00CED1]/10 to-transparent border-[#00CED1]/20">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-[#00CED1]" />
              <div>
                <p className="text-2xl font-bold text-[#00CED1]">{openRequests.length}</p>
                <p className="text-xs text-slate-600">Richieste Aperte</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-[#8B5CF6]/10 to-transparent border-[#8B5CF6]/20">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[#8B5CF6]" />
              <div>
                <p className="text-2xl font-bold text-[#8B5CF6]">{inProgressRequests.length}</p>
                <p className="text-xs text-slate-600">In Creazione</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00CED1] mx-auto"></div>
        </div>
      ) : (
        <>
          {/* Navigation Menu - Custom implementation without Radix Tabs */}
          {!viewMode && (
            <div className="mb-6 flex gap-2 p-1 bg-slate-100 rounded-lg" data-testid="client-dashboard-tabs">
              <button
                data-testid="tab-ordini"
                onClick={() => handleMainTabChange('ordini')}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mainTab === 'ordini'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                }`}
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Ordini
              </button>
              <button
                data-testid="tab-richieste"
                onClick={() => handleMainTabChange('richieste')}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mainTab === 'richieste'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                }`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Richieste
              </button>
            </div>
          )}

          {/* ===================== TAB ORDINI ===================== */}
          {mainTab === 'ordini' && (
            <div className="mt-4">
            <OrdersSectionTabs
              activeOrders={activeOrders}
              completedOrders={completedOrders}
              ordersView={ordersView}
              setOrdersView={setOrdersView}
              renderCartOrderCard={renderCartOrderCard}
              getOrderStatusLabel={getOrderStatusLabel}
              getOrderStatusColor={getOrderStatusColor}
              getTimelineLabel={getTimelineLabel}
              handleMarkDelivered={handleMarkDelivered}
              handleCompleteOrder={handleCompleteOrder}
              handleOpenReviewDialog={handleOpenReviewDialog}
              confirmingDelivery={confirmingDelivery}
              reviewedProducts={reviewedProducts}
            />

            {/* Review Dialog */}
            {selectedOrderForReview && (
              <ReviewDialog
                open={reviewDialogOpen}
                onOpenChange={setReviewDialogOpen}
                order={selectedOrderForReview}
                product={selectedProductForReview}
                onReviewSubmitted={handleReviewSubmitted}
              />
            )}
          </div>
        )}

          {/* ===================== TAB RICHIESTE ===================== */}
          {mainTab === 'richieste' && (
            <div className="mt-4">
            {/* Sub-navigation pills for requests sections - same style as OrdersSectionTabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setRequestsView('open')}
                data-testid="requests-tab-open"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  requestsView === 'open'
                    ? 'bg-[#00CED1] text-white shadow-lg shadow-[#00CED1]/25'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Clock className="w-4 h-4" />
                Richieste aperte
                {openRequests.length > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    requestsView === 'open' ? 'bg-white/20' : 'bg-[#00CED1] text-white'
                  }`}>
                    {openRequests.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setRequestsView('inprogress')}
                data-testid="requests-tab-inprogress"
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  requestsView === 'inprogress'
                    ? 'bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/25'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                In creazione
                {inProgressRequests.length > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    requestsView === 'inprogress' ? 'bg-white/20' : 'bg-[#8B5CF6] text-white'
                  }`}>
                    {inProgressRequests.length}
                  </span>
                )}
              </button>
            </div>

            {/* Content switching with CSS transitions */}
            {/* Open Requests View */}
            {requestsView === 'open' && (
              <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
                {openRequests.length > 0 ? (
                    <div className="space-y-3">
                      {openRequests.map((request, index) => (
                        <motion.div
                          key={request.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card className="p-4 hover:shadow-md transition-shadow" data-testid={`open-request-${request.id}`}>
                            <div className="flex items-start gap-4">
                              <div className="hidden md:block">
                                <CrystalTrackerCompact status={request.status} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h4 className="font-semibold">{request.title}</h4>
                                  <Badge variant="outline">{getStatusLabel(request.status)}</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mb-2 line-clamp-1">{request.description}</p>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Euro className="w-3 h-3" />
                                    {getPriceRangeLabel(request.price_range)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {getTimelineLabel(request.timeline)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" />
                                    {(request.proposals?.[0]?.count || 0)} {(request.proposals?.[0]?.count || 0) === 1 ? 'proposta' : 'proposte'}
                                  </span>
                                </div>
                              </div>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button 
                                    size="sm"
                                    onClick={() => handleViewProposals(request)}
                                    className="bg-[#008080] text-white"
                                    data-testid={`view-proposals-${request.id}`}
                                  >
                                    Vedi Proposte
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle>Proposte per "{request.title}"</DialogTitle>
                                  </DialogHeader>

                                  {loadingProposals ? (
                                    <div className="py-8 text-center">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008080] mx-auto"></div>
                                    </div>
                                  ) : proposals.length > 0 ? (
                                    <div className="space-y-4 mt-4">
                                      {proposals.length >= 2 && (() => {
                                        const withTotals = proposals.map(p => ({
                                          name: p.profiles?.full_name || 'Artigiano',
                                          total: (Number(p.proposed_price) || 0) + (Number(p.shipping_cost) || 0),
                                          timeline: p.estimated_timeline,
                                          rank: timelineToDays(p.estimated_timeline),
                                          freeShip: Number(p.shipping_cost) === 0
                                        }));
                                        const priced = withTotals.filter(p => p.total > 0);
                                        const cheapest = priced.length ? priced.reduce((a, b) => b.total < a.total ? b : a) : null;
                                        const fastest = withTotals.reduce((a, b) => b.rank < a.rank ? b : a);
                                        const freeShipCount = withTotals.filter(p => p.freeShip).length;
                                        return (
                                          <div className="rounded-xl border border-[#008080]/20 bg-[#008080]/5 p-3" data-testid="proposals-comparison-summary">
                                            <p className="text-xs font-semibold text-[#008080] mb-2 uppercase tracking-wide">Confronta al volo · {proposals.length} proposte</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                                              {cheapest && (
                                                <div className="flex items-center gap-2" data-testid="comparison-cheapest">
                                                  <Euro className="w-4 h-4 text-[#008080] flex-shrink-0" />
                                                  <span className="text-slate-700">Più conveniente: <strong>€{cheapest.total.toFixed(2)}</strong> · {cheapest.name}</span>
                                                </div>
                                              )}
                                              <div className="flex items-center gap-2" data-testid="comparison-fastest">
                                                <Clock className="w-4 h-4 text-[#008080] flex-shrink-0" />
                                                <span className="text-slate-700">Più rapida: <strong>{getTimelineLabel(fastest.timeline)}</strong> · {fastest.name}</span>
                                              </div>
                                              {freeShipCount > 0 && (
                                                <div className="flex items-center gap-2" data-testid="comparison-free-shipping">
                                                  <Truck className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                  <span className="text-slate-700">{freeShipCount === 1 ? '1 con spedizione gratuita' : `${freeShipCount} con spedizione gratuita`}</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      {proposals.map((proposal) => (
                                        <div key={proposal.id}>
                                          <Card 
                                            data-testid={`proposal-card-${proposal.id}`}
                                            className={`p-4 transition-all ${
                                              proposal.status === 'accepted' 
                                                ? 'border-[#10B981] bg-[#10B981]/5' 
                                                : previewArtisan?.proposalId === proposal.id 
                                                  ? 'border-[#008080] ring-2 ring-[#008080]/20' 
                                                  : 'hover:shadow-md'
                                            }`}
                                          >
                                            <div className="flex items-start gap-4">
                                              {/* Avatar */}
                                              <div className="flex-shrink-0">
                                                {proposal.profiles?.avatar_url ? (
                                                  <img 
                                                    src={proposal.profiles.avatar_url} 
                                                    alt={proposal.profiles.full_name}
                                                    className="w-14 h-14 rounded-full object-cover border-2 border-white shadow"
                                                  />
                                                ) : (
                                                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#008080] to-[#006666] flex items-center justify-center text-white font-bold text-lg shadow">
                                                    {proposal.profiles?.full_name?.[0] || 'A'}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Content */}
                                              <div className="flex-1 min-w-0">
                                                {/* Header */}
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                  <span className="font-semibold text-slate-800">{proposal.profiles?.full_name}</span>
                                                  {proposal.profiles?.tier === 'pro' && (
                                                    <Badge className="bg-[#DAA520] text-white text-xs">PRO</Badge>
                                                  )}
                                                </div>
                                                
                                                {/* Location */}
                                                {proposal.profiles?.city && (
                                                  <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                                                    <MapPin className="w-3 h-3" />
                                                    {proposal.profiles.city}
                                                  </p>
                                                )}
                                                
                                                {/* Message */}
                                                <p className="text-sm text-slate-600 mb-3 line-clamp-3">{proposal.message}</p>
                                                
                                                {/* Attachments from Crafter */}
                                                {proposal.attachments && proposal.attachments.length > 0 && (
                                                  <div className="mb-3 p-2 bg-slate-50 rounded-lg">
                                                    <p className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-2">
                                                      <Paperclip className="w-3 h-3" />
                                                      Allegati ({proposal.attachments.length})
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                      {proposal.attachments.map((url, idx) => {
                                                        const isPdf = url.toLowerCase().endsWith('.pdf');
                                                        return (
                                                          <div key={idx} className="relative group">
                                                            {isPdf ? (
                                                              <a 
                                                                href={url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 transition-colors"
                                                                data-testid={`proposal-attachment-pdf-${idx}`}
                                                              >
                                                                <FileText className="w-3 h-3" />
                                                                PDF
                                                                <Download className="w-3 h-3" />
                                                              </a>
                                                            ) : (
                                                              <button 
                                                                onClick={() => setLightboxImage(url)}
                                                                className="block relative"
                                                                data-testid={`proposal-attachment-image-${idx}`}
                                                              >
                                                                <img 
                                                                  src={url} 
                                                                  alt={`Allegato ${idx + 1}`}
                                                                  className="w-12 h-12 object-cover rounded border border-slate-200 hover:border-[#008080] transition-colors cursor-pointer"
                                                                />
                                                                <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded transition-opacity">
                                                                  <Eye className="w-4 h-4 text-white" />
                                                                </div>
                                                              </button>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                {/* Price & Timeline & Shipping */}
                                                <div className="flex flex-wrap items-center gap-3 text-sm">
                                                  {proposal.proposed_price > 0 && (
                                                    <span className="flex items-center gap-1 font-bold text-[#008080]">
                                                      <Euro className="w-4 h-4" />
                                                      €{proposal.proposed_price}
                                                    </span>
                                                  )}
                                                  {proposal.shipping_cost !== undefined && proposal.shipping_cost !== null && (
                                                    <span className={`flex items-center gap-1 ${proposal.shipping_cost === 0 ? 'text-green-600' : 'text-slate-500'}`}>
                                                      <Truck className="w-4 h-4" />
                                                      {proposal.shipping_cost === 0 ? 'Spedizione gratuita' : `+€${proposal.shipping_cost} sped.`}
                                                    </span>
                                                  )}
                                                  <span className="flex items-center gap-1 text-slate-500">
                                                    <Clock className="w-4 h-4" />
                                                    {getTimelineLabel(proposal.estimated_timeline)}
                                                  </span>
                                                  {/* Total */}
                                                  {proposal.proposed_price > 0 && proposal.shipping_cost > 0 && (
                                                    <span className="font-semibold text-slate-700 ml-auto">
                                                      Totale: €{(proposal.proposed_price + proposal.shipping_cost).toFixed(2)}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              
                                              {/* Actions */}
                                              <div className="flex flex-col gap-2 flex-shrink-0">
                                                {proposal.status === 'pending' && (
                                                  <>
                                                    <Button 
                                                      size="sm" 
                                                      variant="outline"
                                                      data-testid={`preview-crafter-${proposal.id}`}
                                                      onClick={() => {
                                                        if (previewArtisan?.proposalId === proposal.id) {
                                                          setPreviewArtisan(null);
                                                        } else {
                                                          setPreviewArtisan({ ...proposal.profiles, proposalId: proposal.id });
                                                        }
                                                      }}
                                                      className={`text-[#008080] border-[#008080] hover:bg-[#008080]/10 ${
                                                        previewArtisan?.proposalId === proposal.id ? 'bg-[#008080]/10' : ''
                                                      }`}
                                                    >
                                                      <Store className="w-4 h-4 mr-1" />
                                                      {previewArtisan?.proposalId === proposal.id ? 'Chiudi' : 'Esplora'}
                                                    </Button>
                                                    <Button 
                                                      size="sm" 
                                                      onClick={() => handleAcceptProposal(proposal)} 
                                                      data-testid={`accept-proposal-${proposal.id}`}
                                                      className="bg-[#10B981] hover:bg-[#059669] text-white"
                                                    >
                                                      <CheckCircle2 className="w-4 h-4 mr-1" />
                                                      Accetta
                                                    </Button>
                                                  </>
                                                )}
                                                {proposal.status === 'accepted' && (
                                                  <Badge className="bg-[#10B981] text-white px-3 py-1">
                                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                                    Accettata
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>
                                          </Card>
                                          
                                          {/* Artisan Preview Panel - shown below this specific proposal */}
                                          {previewArtisan?.proposalId === proposal.id && (
                                            <div className="mt-2 ml-4 p-4 bg-gradient-to-r from-[#008080]/5 to-[#DAA520]/5 rounded-xl border border-[#008080]/20">
                                              <div className="flex items-center justify-between mb-3">
                                                <h4 className="font-semibold text-[#008080] flex items-center gap-2">
                                                  <Store className="w-4 h-4" />
                                                  Dettagli Artigiano
                                                </h4>
                                                <Button 
                                                  variant="ghost" 
                                                  size="sm" 
                                                  onClick={() => setPreviewArtisan(null)}
                                                  className="text-slate-500 hover:text-slate-700"
                                                >
                                                  <X className="w-4 h-4" />
                                                </Button>
                                              </div>
                                              
                                              {/* Artisan Info */}
                                              <div className="flex items-start gap-4">
                                                {previewArtisan.avatar_url ? (
                                                  <img 
                                                    src={previewArtisan.avatar_url} 
                                                    alt={previewArtisan.full_name}
                                                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
                                                  />
                                                ) : (
                                                  <div className="w-16 h-16 rounded-full bg-[#008080] flex items-center justify-center text-white font-bold text-xl">
                                                    {previewArtisan.full_name?.[0] || 'A'}
                                                  </div>
                                                )}
                                                <div className="flex-1">
                                                  <p className="font-medium text-slate-800 mb-1">
                                                    {previewArtisan.full_name}
                                                  </p>
                                                  {previewArtisan.city && (
                                                    <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                                                      <MapPin className="w-3 h-3" />
                                                      {previewArtisan.city}{previewArtisan.region ? `, ${previewArtisan.region}` : ''}
                                                    </p>
                                                  )}
                                                  <Link 
                                                    to={crafterUrl(previewArtisan)}
                                                    target="_blank"
                                                    className="inline-flex items-center gap-1 text-sm bg-[#008080] text-white px-3 py-1.5 rounded-lg hover:bg-[#006666] transition-colors"
                                                  >
                                                    Visita vetrina completa
                                                    <ExternalLink className="w-3 h-3" />
                                                  </Link>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="py-8 text-center">
                                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                      <p className="text-slate-600">Nessuna proposta ancora ricevuta</p>
                                      <p className="text-sm text-slate-400 mt-1">Gli artigiani stanno valutando la tua richiesta</p>
                                    </div>
                                  )}
                                </DialogContent>
                              </Dialog>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-12 text-center border-dashed border-2">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-8 h-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessuna richiesta aperta</h3>
                      <p className="text-slate-500 mb-4">Crea una richiesta per ricevere proposte dagli artigiani</p>
                      <Link to="/requests">
                        <Button className="bg-[#00CED1] hover:bg-[#00CED1]/90">
                          <Plus className="w-4 h-4 mr-2" />
                          Crea Richiesta
                        </Button>
                      </Link>
                    </Card>
                  )}
              </div>
            )}

            {/* In Progress Requests View */}
            {requestsView === 'inprogress' && (
              <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
                {inProgressRequests.length > 0 ? (
                    <div className="space-y-3">
                      {inProgressRequests.map((request, index) => (
                        <motion.div
                          key={request.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card className="p-4 border-l-4 border-l-[#8B5CF6] hover:shadow-md transition-shadow bg-gradient-to-r from-[#8B5CF6]/5 to-transparent" data-testid={`in-progress-request-${request.id}`}>
                            <div className="flex items-start gap-4">
                              <div className="hidden md:flex w-12 h-12 rounded-full bg-[#8B5CF6]/10 items-center justify-center flex-shrink-0">
                                <Sparkles className="w-6 h-6 text-[#8B5CF6]" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h4 className="font-semibold">{request.title}</h4>
                                  <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30">
                                    In Lavorazione
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600 mb-2 line-clamp-2">{request.description}</p>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Euro className="w-3 h-3" />
                                    {getPriceRangeLabel(request.price_range)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {getTimelineLabel(request.timeline)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(request.created_at).toLocaleDateString('it-IT')}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  onClick={() => handleResumePayment(request)}
                                  data-testid={`resume-payment-${request.id}`}
                                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                                >
                                  <Euro className="w-4 h-4 mr-1" />
                                  Paga ora
                                </Button>
                                <Link to="/messages">
                                  <Button size="sm" variant="outline" className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10 w-full">
                                    <MessageSquare className="w-4 h-4 mr-1" />
                                    Chat
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-12 text-center border-dashed border-2">
                      <div className="w-16 h-16 bg-[#8B5CF6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-8 h-8 text-[#8B5CF6]" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessuna richiesta in lavorazione</h3>
                      <p className="text-slate-500">Quando accetti una proposta, la richiesta apparirà qui</p>
                    </Card>
                  )}
              </div>
            )}

            {/* Completed/Cancelled Requests Section - Always visible at the bottom */}
            {completedRequests.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-400" />
                  Richieste Concluse ({completedRequests.length})
                </h3>
                <div className="space-y-2">
                  {completedRequests.slice(0, 5).map((request) => (
                    <Card key={request.id} className="p-3 bg-slate-50/50" data-testid={`completed-request-${request.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          request.status === 'completed' ? 'bg-green-100' : 'bg-slate-200'
                        }`}>
                          {request.status === 'completed' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm truncate block">{request.title}</span>
                          <span className="text-xs text-slate-500">{getStatusLabel(request.status)}</span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(request.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </>
      )}
      
      {/* Dialog annullamento ordine (cliente) — processo soft */}
      <Dialog open={!!cancelOrderTarget} onOpenChange={(o) => { if (!o) setCancelOrderTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="client-cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Annullare l'ordine?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Puoi annullare questo ordine perché non è ancora stato spedito. Riceverai un
              <strong> rimborso totale</strong> sul metodo di pagamento originale e l'artigiano verrà avvisato.
              Il rimborso viene accreditato di norma entro <strong>5-10 giorni lavorativi</strong>.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">Motivo dell'annullamento *</label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Facci sapere perché vuoi annullare (obbligatorio)…"
                className="mt-1"
                rows={3}
                data-testid="client-cancel-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCancelOrderTarget(null)} disabled={cancellingOrder}>
                Mantieni l'ordine
              </Button>
              <Button
                onClick={handleCancelOrderClient}
                disabled={cancellingOrder || !cancelReason.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="client-cancel-confirm-btn"
              >
                {cancellingOrder ? 'Annullamento…' : 'Conferma annullamento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox Dialog for attachment images */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => !open && setLightboxImage(null)}>
        <DialogContent className="max-w-4xl w-auto p-2 bg-black/95 border-none">
          <img 
            src={lightboxImage} 
            alt="Allegato ingrandito" 
            className="max-w-full max-h-[85vh] rounded-lg mx-auto"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
